import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  ChevronLeft,
  ChevronRight,
  Mail,
  MessageSquare,
  Sparkles,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import type { SelectedContact } from '@/routes/_public/app'

interface MessageComposerProps {
  selectedContacts: SelectedContact[]
  initialMessage?: string
  initialSubject?: string
  initialSmsMessage?: string
  initialSignature?: string
  onComplete: (payload: {
    message: string
    subject: string
    smsMessage: string
    signature: string
  }) => void
}

export function MessageComposer({
  selectedContacts,
  initialMessage = '',
  initialSubject = '',
  initialSmsMessage = '',
  initialSignature = '',
  onComplete,
}: MessageComposerProps) {
  const [message, setMessage] = useState(initialMessage)
  const [subject, setSubject] = useState(initialSubject)
  const [smsMessage, setSmsMessage] = useState(initialSmsMessage)
  const [smsEdited, setSmsEdited] = useState(initialSmsMessage !== '' && initialSmsMessage !== initialMessage)
  const [signature, setSignature] = useState(initialSignature)
  const [showSmsEditor, setShowSmsEditor] = useState(false)
  const [previewIndex, setPreviewIndex] = useState(0)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const subjectRef = useRef<HTMLInputElement>(null)
  const signatureRef = useRef<HTMLTextAreaElement>(null)

  const insertMergeTag = (target: 'message' | 'subject' | 'signature') => {
    const mergeTag = '{{firstName}}'
    if (target === 'message') {
      const textarea = textareaRef.current
      if (!textarea) return
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newValue =
        message.substring(0, start) + mergeTag + message.substring(end)
      setMessage(newValue)
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + mergeTag.length, start + mergeTag.length)
      }, 0)
      return
    }

    if (target === 'signature') {
      const textarea = signatureRef.current
      if (!textarea) return
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      const newValue =
        signature.substring(0, start) + mergeTag + signature.substring(end)
      setSignature(newValue)
      setTimeout(() => {
        textarea.focus()
        textarea.setSelectionRange(start + mergeTag.length, start + mergeTag.length)
      }, 0)
      return
    }

    const input = subjectRef.current
    if (!input) return
    const start = input.selectionStart ?? subject.length
    const end = input.selectionEnd ?? subject.length
    const newValue =
      subject.substring(0, start) + mergeTag + subject.substring(end)
    setSubject(newValue)
    setTimeout(() => {
      input.focus()
      input.setSelectionRange(start + mergeTag.length, start + mergeTag.length)
    }, 0)
  }

  const handleContinue = () => {
    if (!message.trim()) {
      alert('Please write a message.')
      return
    }
    onComplete({
      message,
      subject,
      smsMessage: smsMessage || message,
      signature,
    })
  }

  // Calculate character count (informational for SMS awareness)
  const charCount = message.length

  const hasMergeField =
    message.includes('{{firstName}}') || subject.includes('{{firstName}}')
  const smsContacts = selectedContacts.filter(
    (contact) => contact.sendSms && contact.phone,
  )
  const emailContacts = selectedContacts.filter(
    (contact) => contact.sendEmail && contact.email,
  )
  const hasEmailContacts = emailContacts.length > 0
  const hasPhoneContacts = smsContacts.length > 0
  const previewContacts = hasEmailContacts ? emailContacts : smsContacts
  const totalPreviewContacts = previewContacts.length
  const isSmsOnly = hasPhoneContacts && !hasEmailContacts
  const isEmailOnly = hasEmailContacts && !hasPhoneContacts

  useEffect(() => {
    if (previewIndex > totalPreviewContacts - 1) {
      setPreviewIndex(0)
    }
  }, [previewIndex, totalPreviewContacts])

  useEffect(() => {
    if (!message.trim()) {
      setSmsMessage('')
      setSmsEdited(false)
      return
    }
    if (smsEdited) return
    setSmsMessage(message)
  }, [message, smsEdited])

  useEffect(() => {
    if (!hasMergeField || totalPreviewContacts <= 1) return
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        setPreviewIndex((idx) =>
          idx === 0 ? totalPreviewContacts - 1 : idx - 1,
        )
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault()
        setPreviewIndex((idx) =>
          idx === totalPreviewContacts - 1 ? 0 : idx + 1,
        )
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [hasMergeField, totalPreviewContacts])

  // Preview with selected contact
  const previewContact = previewContacts[previewIndex]
  const previewMessage = previewContact
    ? message.replace(/\{\{firstName\}\}/g, getFirstName(previewContact.name))
    : message
  const previewSignature = previewContact
    ? signature.replace(/\{\{firstName\}\}/g, getFirstName(previewContact.name))
    : signature
  const previewEmailBody = appendSignature(previewMessage, previewSignature)
  const previewSubject = previewContact
    ? subject.replace(/\{\{firstName\}\}/g, getFirstName(previewContact.name))
    : subject
  const resolvedSmsMessage = previewContact
    ? smsMessage.replace(/\{\{firstName\}\}/g, getFirstName(previewContact.name))
    : smsMessage
  const smsPreview = truncateText(
    resolvedSmsMessage || previewMessage,
    160,
  )
  const smsCharCount = (resolvedSmsMessage || '').length
  const smsSegments = Math.ceil(smsCharCount / 160) || 1

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h1 className="text-3xl font-bold">Compose Your Message</h1>
        <p className="text-slate-600">
          Write once, personalize for each recipient.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Message Editor</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasEmailContacts && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <Mail className="h-4 w-4" />
                Email Message
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="message-subject">Subject</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => insertMergeTag('subject')}
                    type="button"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Insert Name
                  </Button>
                </div>
                <Input
                  id="message-subject"
                  ref={subjectRef}
                  placeholder="Subject line for email"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                />
                <div className="text-xs text-slate-600">
                  Use{' '}
                  <code className="bg-slate-100 px-1 rounded">
                    {'{{firstName}}'}
                  </code>{' '}
                  to personalize the subject.
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="message-textarea">Email Body</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => insertMergeTag('message')}
                    type="button"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Insert Name
                  </Button>
                </div>
                <Textarea
                  id="message-textarea"
                  ref={textareaRef}
                  placeholder="Hi {{firstName}}, just wanted to let you know..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
                <div className="text-xs text-slate-600">
                  Use{' '}
                  <code className="bg-slate-100 px-1 rounded">
                    {'{{firstName}}'}
                  </code>{' '}
                  to personalize.
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="message-signature">Signature (optional)</Label>
                </div>
                <Textarea
                  id="message-signature"
                  ref={signatureRef}
                  placeholder={`Sincerely,

Cynthia Example
Group Leader
555-343-3433`}
                  value={signature}
                  onChange={(e) => setSignature(e.target.value)}
                  rows={4}
                  className="resize-none"
                />
                {!isEmailOnly && (
                  <div className="text-xs text-slate-600">
                    Signature is appended to email only, not SMS.
                  </div>
                )}
              </div>
            </div>
          )}

          {hasPhoneContacts && !hasEmailContacts && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-4">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                <MessageSquare className="h-4 w-4" />
                Text Message
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="message-textarea">Text Body</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => insertMergeTag('message')}
                    type="button"
                  >
                    <Sparkles className="h-4 w-4 mr-1" />
                    Insert Name
                  </Button>
                </div>
                <Textarea
                  id="message-textarea"
                  ref={textareaRef}
                  placeholder="Hi {{firstName}}, just wanted to let you know..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
                <div className="flex justify-between text-xs text-slate-600">
                  <span>
                    Use{' '}
                    <code className="bg-slate-100 px-1 rounded">
                      {'{{firstName}}'}
                    </code>{' '}
                    to personalize.
                  </span>
                  <span>
                    {charCount} characters ({smsSegments} SMS segment
                    {smsSegments !== 1 ? 's' : ''})
                  </span>
                </div>
              </div>
            </div>
          )}

          {hasPhoneContacts && (
            <div className="bg-slate-50 p-4 rounded-lg space-y-2 border border-slate-200">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm">
                  {isSmsOnly && previewContact && hasMergeField && totalPreviewContacts > 1
                    ? `Text Preview for ${previewContact.name}`
                    : 'Text Preview'}
                </p>
                {isSmsOnly && hasMergeField && totalPreviewContacts > 1 && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500 mr-1">
                      {previewIndex + 1} / {totalPreviewContacts}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      aria-label="Previous preview"
                      onClick={() =>
                        setPreviewIndex((idx) =>
                          idx === 0 ? totalPreviewContacts - 1 : idx - 1,
                        )
                      }
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      aria-label="Next preview"
                      onClick={() =>
                        setPreviewIndex((idx) =>
                          idx === totalPreviewContacts - 1 ? 0 : idx + 1,
                        )
                      }
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                {!isSmsOnly && (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={() => setShowSmsEditor((prev) => !prev)}
                  >
                    {showSmsEditor ? 'Hide Editor' : 'Edit Text Version'}
                  </Button>
                )}
              </div>
              {!isSmsOnly && (
                <p className="text-xs text-slate-600">
                  SMS uses the message body only (no subject or signature).
                </p>
              )}
              {!isSmsOnly && showSmsEditor && (
                <div className="space-y-2">
                  <Textarea
                    value={smsMessage}
                    onChange={(e) => {
                      setSmsMessage(e.target.value)
                      setSmsEdited(true)
                    }}
                    rows={4}
                    className="resize-none"
                    placeholder="Optional: customize the text-only version"
                  />
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span>
                      {smsCharCount} characters ({smsSegments} SMS segment
                      {smsSegments !== 1 ? 's' : ''})
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      type="button"
                      onClick={() => {
                        setSmsMessage(message)
                        setSmsEdited(false)
                      }}
                    >
                      Reset to Smart Version
                    </Button>
                  </div>
                </div>
              )}
              <div className="bg-white p-3 rounded border text-sm whitespace-pre-wrap">
                {smsPreview || 'Start typing your message to see a preview.'}
              </div>
              <div className="text-xs text-slate-600">
                {smsCharCount} characters ({smsSegments} SMS segment
                {smsSegments !== 1 ? 's' : ''})
              </div>
            </div>
          )}

          {/* Preview */}
          {hasEmailContacts && previewContact && message && (
            <div className="bg-slate-50 p-4 rounded-lg space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm">
                  Email Preview for {previewContact.name}:
                </p>
                {hasMergeField && totalPreviewContacts > 1 && (
                  <div className="flex items-center gap-1">
                    <span className="text-xs text-slate-500 mr-1">
                      {previewIndex + 1} / {totalPreviewContacts}
                    </span>
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      aria-label="Previous preview"
                      onClick={() =>
                        setPreviewIndex((idx) =>
                          idx === 0 ? totalPreviewContacts - 1 : idx - 1,
                        )
                      }
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="icon"
                      type="button"
                      aria-label="Next preview"
                      onClick={() =>
                        setPreviewIndex((idx) =>
                          idx === totalPreviewContacts - 1 ? 0 : idx + 1,
                        )
                      }
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
              {previewSubject && (
                <div className="text-xs text-slate-500 space-y-1">
                  <div>Subject:</div>
                  <div className="bg-white px-2 py-1 rounded border text-slate-700">
                    {previewSubject}
                  </div>
                </div>
              )}
              <div className="bg-white p-3 rounded border text-sm whitespace-pre-wrap">
                {previewEmailBody}
              </div>
            </div>
          )}

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg text-sm text-blue-900">
            <p className="font-semibold mb-1">How it works:</p>
            <p>
              Your message will be personalized for each recipient. When you tap
              their name, a draft will open in your{' '}
              {isEmailOnly ? 'email app' : 'messaging app'} with their
              personalized message ready to send.
            </p>
          </div>

          <Button
            onClick={handleContinue}
            className="w-full"
            size="lg"
            disabled={!message.trim()}
          >
            Continue to Send
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

function getFirstName(fullName: string): string {
  return fullName.split(' ')[0] || fullName
}

function appendSignature(messageBody: string, signatureText: string): string {
  if (!signatureText.trim()) return messageBody
  const trimmedBody = messageBody.trimEnd()
  const trimmedSignature = signatureText.trim()
  const separator = trimmedBody ? '\n\n' : ''
  return `${trimmedBody}${separator}${trimmedSignature}`.trimEnd()
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return `${text.slice(0, Math.max(0, maxLength - 3)).trimEnd()}...`
}
